/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\aiToolProfilesApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/


import { api } from '../../../lib/api';

const BASE = '/api/telephony/ai-agents/tool-profiles';

export interface AiToolProfile {
    id: number;
    uuid: string;
    domainId: number;
    name: string;
    description: string | null;
    enabled: boolean;
    authType?: string | null;
    authHeader?: string | null;
    authValue?: string | null;
    baseUrl?: string | null;
    toolCount?: number;
    createdAt: string | null;
    updatedAt: string | null;
}

export interface AiToolProfileInput {
    name: string;
    description?: string | null;
    enabled?: boolean;
    authType?: string | null;
    authHeader?: string | null;
    authValue?: string | null;
    baseUrl?: string | null;
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

export async function listToolProfiles(params?: { search?: string; page?: number; limit?: number }) {
    const q = new URLSearchParams();
    if (params?.search) q.set('search', params.search);
    if (params?.page) q.set('page', String(params.page));
    if (params?.limit) q.set('limit', String(params.limit));
    const resp = await api.get(`${BASE}?${q.toString()}`);
    return resp.data as { data: AiToolProfile[]; meta: any };
}

export async function getToolProfileById(id: number): Promise<AiToolProfile> {
    const resp = await api.get(`${BASE}/${id}`);
    return resp.data.data;
}

export async function createToolProfile(input: AiToolProfileInput): Promise<AiToolProfile> {
    const resp = await api.post(BASE, input);
    return resp.data.data;
}

export async function updateToolProfile(id: number, input: Partial<AiToolProfileInput>): Promise<AiToolProfile> {
    const resp = await api.put(`${BASE}/${id}`, input);
    return resp.data.data;
}

export async function deleteToolProfile(id: number): Promise<void> {
    await api.delete(`${BASE}/${id}`);
}

// ── Tools in Profile ──────────────────────────────────────────────────────────

export async function getProfileToolItems(profileId: number) {
    const resp = await api.get(`${BASE}/${profileId}/tools`);
    return resp.data.data;
}

export async function setProfileToolItems(profileId: number, toolIds: number[]): Promise<void> {
    await api.put(`${BASE}/${profileId}/tools`, { toolIds });
}

// ── Quicklist for agent form dropdown ────────────────────────────────────────

export async function listToolProfilesQuickList(): Promise<{ id: number; name: string }[]> {
    const resp = await api.get('/api/telephony/ai-agents/tool-profiles/quicklist');
    return resp.data.data;
}
